import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import ClientLayout from "../../layouts/ClientLayout";
import EngineerLayout from "../../layouts/EngineerLayout";
import API from "../../services/api";

// ── DESIGN SYSTEM ────────────────────────────────────────────────────────────
const DS = {
  indigo:       "#4F46E5",
  indigoMid:    "#6366F1",
  indigoLight:  "#EEF2FF",
  indigoDark:   "#3730A3",
  teal:         "#0D9488",
  tealLight:    "#F0FDFA",
  emerald:      "#059669",
  emeraldLight: "#ECFDF5",
  amber:        "#D97706",
  amberLight:   "#FFFBEB",
  rose:         "#E11D48",
  roseLight:    "#FFF1F2",
  violet:       "#7C3AED",
  violetLight:  "#F5F3FF",
  orange:       "#EA580C",
  orangeLight:  "#FFF7ED",
  bg:           "#F8FAFC",
  surface:      "#FFFFFF",
  surfaceAlt:   "#F8FAFC",
  border:       "#E2E8F0",
  dark:         "#0F172A",
  gray:         "#64748B",
  grayLight:    "#F1F5F9",
  faint:        "#94A3B8",
};

export default function DiscussionModule({ layout = "admin" }) {
  const { id: urlProjectId } = useParams();
  const [currentProject, setCurrentProject] = useState(null);
  const [comments, setComments]             = useState([]);
  const [topics, setTopics]                 = useState([]);
  const [formData, setFormData]             = useState({ topic: "", comment: "" });

  const isEngineer = layout === "engineer";
  const isClient   = layout === "client";

  const Layout =
    layout === "client"   ? ClientLayout   :
    layout === "engineer" ? EngineerLayout :
                            AdminLayout;

  // ── ROOT CAUSE FIX ──────────────────────────────────────────────────────────
  // 1. DB `discussions.project_id` is the numeric integer FK matching
  //    `projects.id`, NOT the varchar `project_uid`.
  //    The original code passed `project_uid` (e.g. "AP-PUNE-001") to both
  //    the fetch endpoint and the POST body — so fetches returned nothing
  //    and inserts linked to the wrong / null project.
  //
  // 2. Engineer fetch called fetchDiscussions(res.data.project.project_uid)
  //    — wrong field.  Fix: use urlProjectId (numeric id from the URL) directly.
  //    No need to load the full project object just to get the id we already have.
  //
  // 3. handleSave read localStorage("project_id") as fallback for engineer —
  //    that key holds the CLIENT's project UID, not the engineer's numeric id.
  //    Fix: always use urlProjectId for engineer mode.
  // ────────────────────────────────────────────────────────────────────────────

  // Fetch discussions scoped to a numeric project id
  const fetchDiscussions = async (numericProjectId) => {
    try {
      const res = await API.get(`/discussions/project/${numericProjectId}`);
      if (res.data?.success) {
        setComments(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching discussions:", err);
      setComments([]);
    }
  };

  const fetchAllDiscussions = async () => {
    try {
      const res = await API.get("/admin/discussions");
      if (res.data?.success) {
        setComments(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching all discussions:", err);
      setComments([]);
    }
  };

  // Fetch discussion topics once on mount
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await API.get("/admin/discussion-topics");
        if (res.data?.success) {
          setTopics(res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching discussion topics:", err);
      }
    };
    fetchTopics();
  }, []);

  // Fetch discussions based on role/context
  useEffect(() => {
    if (isEngineer && urlProjectId) {
      // Use the numeric project id from the URL directly — no need to resolve
      // project_uid because the DB discussions table FK is the integer id.
      fetchDiscussions(urlProjectId);

      // Still fetch project meta so we can display the project title in the header
      API.get(`/projects/${urlProjectId}`)
        .then(res => {
          if (res.data?.success) setCurrentProject(res.data.project);
        })
        .catch(err => console.error("Error fetching project meta:", err));

    } else if (isClient) {
      // Client stores their numeric project id in localStorage at login
      const clientProjectId = localStorage.getItem("project_id");
      if (clientProjectId) fetchDiscussions(clientProjectId);

    } else {
      fetchAllDiscussions();
    }
  }, [urlProjectId, isEngineer, isClient]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!formData.topic || !formData.comment.trim()) {
      alert("Please select a topic and write a comment.");
      return;
    }

    // Resolve the correct numeric project id for the POST body
    let targetProjectId;
    if (isEngineer) {
      // urlProjectId is already the numeric DB id — use it directly
      targetProjectId = urlProjectId;
    } else if (isClient) {
      targetProjectId = localStorage.getItem("project_id");
    } else {
      // Admin: no project scoping needed; send null or omit
      targetProjectId = null;
    }

    try {
      const response = await API.post("/admin/discussions", {
        project_id: targetProjectId,
        topic_id:   formData.topic,
        user_id:    localStorage.getItem("id"),
        comment:    formData.comment.trim(),
      });

      if (response.data.success) {
        alert("Discussion Saved Successfully");
        setFormData({ topic: "", comment: "" });

        // Refresh the feed with the same scoped fetch
        if (isEngineer && urlProjectId) {
          fetchDiscussions(urlProjectId);
        } else if (isClient) {
          fetchDiscussions(localStorage.getItem("project_id"));
        } else {
          fetchAllDiscussions();
        }
      }
    } catch (error) {
      console.error(error);
      alert("Error saving discussion");
    }
  };

  return (
    <Layout>
      <div style={{ flex: 1, padding: "28px 28px", overflowY: "auto" }}>

        {/* PAGE HEADER */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💬</div>
          <div>
            <h1 style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 26, color: DS.dark, margin: 0 }}>Discussion</h1>
            <div style={{ fontSize: 13, color: DS.gray, marginTop: 4 }}>
              {isEngineer && currentProject
                ? `Discussion for project: ${currentProject.title}`
                : "Share feedback, ask questions, or post updates on active discussion topics"}
            </div>
          </div>
        </div>

        {/* DISCUSSION FORM */}
        <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#E11D48,#D97706)" }} />

          <div style={{ padding: "24px 26px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>✍️</div>
              <div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 16, color: DS.dark }}>Add Comment</div>
                <div style={{ fontSize: 12, color: DS.gray }}>Choose a topic and enter your discussion comments</div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Select Topic */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em" }}>Discussion Topic</label>
                <select name="topic" value={formData.topic} onChange={handleChange}
                  style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "inherit", boxSizing: "border-box", height: "43px" }}
                  onFocus={e => e.target.style.borderColor = DS.indigo}
                  onBlur={e  => e.target.style.borderColor = DS.border}>
                  <option value="">Select Topic</option>
                  {topics.map(topic => (
                    <option key={topic.id} value={topic.id}>{topic.title}</option>
                  ))}
                </select>
              </div>

              {/* Comment Textarea */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em" }}>Comment Message</label>
                <textarea rows="5" name="comment" value={formData.comment} onChange={handleChange}
                  placeholder="Write discussion comment..."
                  style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }}
                  onFocus={e => e.target.style.borderColor = DS.indigo}
                  onBlur={e  => e.target.style.borderColor = DS.border} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              <button onClick={handleSave}
                style={{ background: "linear-gradient(135deg,#4F46E5,#6366F1)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 14px rgba(79,70,229,0.3)" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(79,70,229,0.45)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 14px rgba(79,70,229,0.3)"}>
                Post Discussion
              </button>
            </div>
          </div>
        </div>

        {/* DISCUSSION COMMENTS FEED */}
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 18, color: DS.dark, marginBottom: 16 }}>Discussion Feed</h2>
          {comments.length === 0 ? (
            <div style={{ background: DS.surface, border: `1.5px solid ${DS.border}`, borderRadius: 18, padding: 24, textAlign: "center", color: DS.faint, fontSize: 14 }}>
              No comments posted yet. Start the conversation!
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {comments.map(c => (
                <div key={c.id} style={{ background: DS.surface, border: `1.5px solid ${DS.border}`, borderRadius: 18, padding: 18, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: DS.indigo, background: DS.indigoLight, padding: "4px 8px", borderRadius: 8 }}>
                        {c.topic || "General"}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: DS.dark }}>
                        {c.posted_by || "User"}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: DS.faint }}>
                      {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: DS.dark, lineHeight: 1.5 }}>
                    {c.comment}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}